import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid, GridItem } from "@chakra-ui/react";
import { MdPersonSearch, MdErrorOutline } from "react-icons/md";
import UsersList from "./usersList";
import UsersRoleTabs from "./UsersRoleTabs";
import UsersStatsStrip from "./UsersStatsStrip";
import useUsersBrowse from "./hooks/useUsersBrowse";
import useUserDetail from "./hooks/useUserDetail";
import useLastGroups from "../groupsManage/hooks/useLastGroups";
import useGroupSearch from "../groupsManage/hooks/useGroupSearch";
import { InspectorCentered } from "./inspector/InspectorShell";
import UserInspector from "./inspector/UserInspector";
import GroupInspector from "./inspector/GroupInspector";

/**
 * Picks the active group collection: search results when querying, else the
 * recent-groups list.
 * @param {Object} groupSearch - useGroupSearch state
 * @param {Object} lastGroups - useLastGroups state
 * @returns {{items: Array, loading: boolean, error: any}}
 */
function pickGroupList(groupSearch, lastGroups) {
    if (groupSearch.hasQuery) return { items: groupSearch.results, loading: groupSearch.loading, error: groupSearch.error };
    return { items: lastGroups.groups, loading: lastGroups.loading, error: lastGroups.error };
}

/**
 * Right-column inspector view: empty / user (lazy detail) / group, all wrapped
 * in the shared fixed-height shell.
 * @param {Object} props - { selected, adminRight, userDetail, onClear, onGroupsChanged }
 * @returns {React.ReactElement}
 */
function InspectorView({ selected, adminRight, userDetail, onClear, onGroupsChanged }) {
    if (!selected) return <InspectorCentered icon={MdPersonSearch} label="Sélectionnez un utilisateur ou un groupe" />;
    if (selected.type === "group") {
        return <GroupInspector groupId={selected.id} userRight={adminRight} onChanged={onGroupsChanged}
            onDeleted={() => { onGroupsChanged(); onClear(); }} onClose={onClear} />;
    }
    if (userDetail.loading) return <InspectorCentered spin label="Chargement du profil…" />;
    if (userDetail.error) return <InspectorCentered icon={MdErrorOutline} tone="red.400" label={userDetail.error} />;
    if (!userDetail.detail) return <InspectorCentered icon={MdPersonSearch} label="Sélectionnez un utilisateur" />;
    return <UserInspector data={userDetail.detail} adminRight={adminRight} />;
}

/**
 * Main page for managing auditors, artists and groups in one console.
 * Search-first lightweight lists on the left; a single fixed-height, internally
 * scrolling inspector on the right shows the selected user OR group on demand.
 * @returns {React.ReactElement}
 */
function AuditorProAndArtistes() {
    const [adminRight, setAdminRight] = useState(1);
    const [roleFilter, setRoleFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const isGroups = roleFilter === "groups";
    const browse = useUsersBrowse({ query: isGroups ? "" : query, roleFilter: isGroups ? "all" : roleFilter, reloadKey });
    const userDetail = useUserDetail();
    const lastGroups = useLastGroups(isGroups);
    const groupSearch = useGroupSearch();

    /** Fetches the connected admin's right level. */
    const fetchAdminRight = useCallback(async () => {
        try { setAdminRight((await axios.get("profiles/my_profile")).data["right"]); }
        catch (error) { console.error("Error fetching admin profile:", error); }
    }, []);
    useEffect(() => { fetchAdminRight().then(() => null); }, [fetchAdminRight]);

    /** @param {string} value - New role tab value */
    const handleTab = (value) => { setRoleFilter(value); setSelected(null); userDetail.clear(); };

    /** @param {Object} item - Selected row (user row or group object) */
    const handleSelect = (item) => {
        if (isGroups) { setSelected({ type: "group", id: item.id }); userDetail.clear(); }
        else { setSelected({ type: "user", id: item.id, email: item.email }); userDetail.loadDetail(item); }
    };

    const listData = isGroups ? pickGroupList(groupSearch, lastGroups)
        : { items: browse.items, loading: browse.loading, error: browse.error };

    return (
        <Box p="4px" pb="20px">
            <UsersStatsStrip onRoleSelect={handleTab} />
            <Grid templateColumns={{ base: "1fr", md: "repeat(12, 1fr)" }} gap="20px" alignItems="start">
                <GridItem colSpan={{ base: 12, md: 4 }}>
                    <UsersRoleTabs value={roleFilter} onChange={handleTab} />
                    <UsersList mode={isGroups ? "groups" : "users"}
                        title={isGroups ? "Liste des Groupes" : "Liste des Utilisateurs"}
                        footerLabel={isGroups ? "Total Groupes" : "Total Utilisateurs"}
                        items={listData.items} loading={listData.loading} error={listData.error}
                        query={isGroups ? groupSearch.query : query}
                        onQueryChange={isGroups ? groupSearch.setQuery : setQuery}
                        roleFilter={roleFilter} selectedId={selected ? selected.id : null} onSelect={handleSelect}
                        onRefresh={() => (isGroups ? lastGroups.refresh() : setReloadKey((k) => k + 1))}
                        showSearchPrompt={!isGroups && !browse.active} />
                </GridItem>
                <GridItem colSpan={{ base: 12, md: 8 }}>
                    <InspectorView selected={selected} adminRight={adminRight} userDetail={userDetail}
                        onClear={() => { setSelected(null); userDetail.clear(); }}
                        onGroupsChanged={() => lastGroups.refresh()} />
                </GridItem>
            </Grid>
        </Box>
    );
}

export default AuditorProAndArtistes;
